import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiError, fetchCurrentUser, getAccessToken, login, logout } from '@srichaitra/api';
import { errorMessageFor, successMessages } from '@srichaitra/constants';

/**
 * Authentication state: who is signed in, and where we are in finding out.
 *
 * `status` is a single state machine rather than a set of booleans. The
 * distinction that matters is 'restoring' — the app has a stored token and is
 * checking it — because during that moment the user is neither signed in nor
 * signed out, and rendering the login screen would flash it in front of someone
 * who is already authenticated.
 *
 *   idle       nothing tried yet
 *   restoring  a stored token is being verified against auth.me
 *   signingIn  credentials submitted, waiting
 *   signedIn   `user` is populated
 *   signedOut  no session; the login screen is correct
 */

const initialState = {
  status: 'idle',
  user: null,
  error: null,
  /** Set when a session ended on its own, so the login screen can explain why. */
  sessionExpired: false
};

/** Normalises any thrown error into the shape the reducers store. */
function asRejection(error) {
  if (error instanceof ApiError) {
    return { code: error.code, message: error.userMessage };
  }
  return { code: 'UNKNOWN_ERROR', message: errorMessageFor('UNKNOWN_ERROR') };
}

export const signIn = createAsyncThunk('auth/signIn', async (credentials, { rejectWithValue }) => {
  try {
    return await login(credentials);
  } catch (error) {
    return rejectWithValue(asRejection(error));
  }
});

/**
 * Verifies a token left over from a previous visit.
 *
 * With no stored token this rejects immediately rather than calling auth.me,
 * which would be a guaranteed 401 on every cold start.
 *
 * The check is inside the thunk rather than in `condition` on purpose. A
 * condition that returns false makes createAsyncThunk skip the thunk without
 * dispatching anything at all — so `status` would sit at 'idle' forever, and
 * the route guards, which wait while the status is still settling, would show a
 * loading spinner instead of the login screen. Rejecting reaches the reducer
 * and moves the machine to 'signedOut', which is the truthful answer.
 */
export const restoreSession = createAsyncThunk('auth/restoreSession', async (_, { rejectWithValue }) => {
  if (!getAccessToken()) return rejectWithValue(null);

  try {
    return await fetchCurrentUser();
  } catch (error) {
    return rejectWithValue(asRejection(error));
  }
});

export const signOut = createAsyncThunk('auth/signOut', async () => {
  // logout() clears local tokens even when the server call fails, so this
  // always resolves and the user always ends up signed out on this device.
  await logout();
  return successMessages.loggedOut;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Dispatched by the API interceptor when a refresh could not be recovered. */
    sessionExpired(state) {
      state.status = 'signedOut';
      state.user = null;
      state.error = null;
      state.sessionExpired = true;
    },
    /** Clears the error banner — e.g. when the user edits the form again. */
    clearAuthError(state) {
      state.error = null;
      state.sessionExpired = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = 'signingIn';
        state.error = null;
        state.sessionExpired = false;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.status = 'signedIn';
        state.user = action.payload;
        state.error = null;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = 'signedOut';
        state.user = null;
        state.error = action.payload ?? asRejection(null);
      })

      .addCase(restoreSession.pending, (state) => {
        state.status = 'restoring';
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.status = 'signedIn';
        state.user = action.payload;
      })
      .addCase(restoreSession.rejected, (state) => {
        // No error banner here. A token that expired while the tab was closed
        // is the ordinary case, not something the user did wrong.
        state.status = 'signedOut';
        state.user = null;
      })

      .addCase(signOut.fulfilled, (state) => {
        state.status = 'signedOut';
        state.user = null;
        state.error = null;
        state.sessionExpired = false;
      });
  }
});

export const { sessionExpired, clearAuthError } = authSlice.actions;
export const authReducer = authSlice.reducer;

/* --- Selectors ---------------------------------------------------------
 * Components read state through these, never by reaching into the shape
 * directly, so the shape stays free to change. */

export const selectAuthStatus = (state) => state.auth.status;
export const selectCurrentUser = (state) => state.auth.user;
export const selectAuthError = (state) => state.auth.error;
export const selectSessionExpired = (state) => state.auth.sessionExpired;
export const selectIsSignedIn = (state) => state.auth.status === 'signedIn';
export const selectIsAuthPending = (state) =>
  state.auth.status === 'restoring' || state.auth.status === 'signingIn';

/** The sections this user's role grants. Always an array, so callers need no guard. */
export const selectTiles = (state) => state.auth.user?.tiles ?? [];

/**
 * Whether this account is likely to be allowed the full business figures.
 *
 * Mirrors the rule the server enforces on finance.businessStatus: the admin
 * tile *and* a built-in role, which in practice means Super Admin. A role
 * invented inside the app can be granted the admin tile, so the tile alone is
 * not enough — `roles.is_system` is set only by the seed and the migration.
 *
 * This decides which call to make, nothing more. The restriction itself lives
 * on the server, and this selector agreeing with it is a convenience, not a
 * control: if the two ever disagree, the server wins and the dashboard falls
 * back to the summary.
 */
export const selectCanSeeBusinessStatus = (state) =>
  Boolean(state.auth.user?.roleIsSystem) && (state.auth.user?.tiles ?? []).includes('admin');
